import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  Flex,
  Input,
  FormControl,
  FormLabel,
  Button,
  Select,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { searchLead } from '../../redux/actions/Files.action';
import { getSetting } from '../../redux/actions/Setting.action';
import { allCounselorDetail } from '../../redux/actions/Files.action';

const TotalLeadsChart = () => {
  const [leadsData, setLeadsData] = useState([]);
  const [xCategories, setXCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [status, setStatus] = useState('');
  const [managerId, setManagerId] = useState('');
  const getFile = useSelector((state) => state.files);
  const setting = useSelector((state) => state.setting);
  const managers = getFile?.allCounselor?.data?.CounselorsData || [];

  const totalLeads = getFile?.getAllLead?.data?.totalCount || 0;

  const chartBg = useColorModeValue('white', 'gray.800');
  const chartColor = useColorModeValue('#38A169', '#90CDF4');
  const tooltipTheme = useColorModeValue('light', 'dark');

  const counts = useSelector((state) => state.admin);
  const countData = counts?.masterCount?.data;

  // Fetch settings when component mounts
  useEffect(() => {
    dispatch(getSetting());
  }, [dispatch]);
  // Add this useEffect to fetch counselor details
  useEffect(() => {
    dispatch(allCounselorDetail());
  }, [dispatch]);
  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  const handleManagerChange = (e) => {
    const managerId = e.target.value;
    setManagerId(managerId);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const searchParams = {
          startDate,
          endDate,
          status: status || undefined,
          managerId: managerId || undefined,
          page: 1,
          limit: 10,
        };

        // Remove undefined values from searchParams
        Object.keys(searchParams).forEach((key) => {
          if (searchParams[key] === undefined) {
            delete searchParams[key];
          }
        });

        const queryParams = new URLSearchParams(searchParams).toString();
        await dispatch(searchLead(queryParams));
      } catch (error) {
        console.error('Error fetching lead data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, status, managerId, dispatch]); // Add managerId to dependencies

  useEffect(() => {
    if (!getFile?.getAllLead?.data?.PatientData) {
      setIsLoading(false);
      return;
    }

    try {
      const dates = [];
      let currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);

      while (currentDate <= endDateObj) {
        dates.push(new Date(currentDate).toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const leadCounts = dates.map((date) => {
        const filteredLeads = getFile.getAllLead.data.PatientData.filter(
          (lead) => {
            const leadDate = new Date(lead.createdAt)
              .toISOString()
              .split('T')[0];
            const matchesDate = leadDate === date;
            const matchesStatus = !status || lead.leadStatus === status;
            const matchesManager =
              !managerId ||
              (lead.counselorAssign && lead.counselorAssign._id === managerId);
            return matchesDate && matchesStatus && matchesManager;
          },
        );
        return filteredLeads.length;
      });

      setLeadsData(leadCounts);
      setXCategories(dates);
    } catch (error) {
      console.error('Error processing lead data:', error);
      setLeadsData([]);
    }
  }, [
    getFile?.getAllLead?.data?.PatientData,
    startDate,
    endDate,
    status,
    managerId,
  ]); // Add managerId here

  const handleStartDateChange = (e) => {
    if (new Date(e.target.value) > new Date(endDate)) return;
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    if (new Date(e.target.value) < new Date(startDate)) return;
    setEndDate(e.target.value);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const chartData = [
    {
      name: 'Total Leads',
      data: leadsData,
    },
  ];

  const chartOptions = {
    chart: {
      id: 'leads-chart',
      toolbar: { show: true },
      zoom: { enabled: false },
      offsetX: 0,
      offsetY: 10,
    },
    xaxis: {
      categories: xCategories,
      title: { text: 'Date' },
      tickPlacement: 'on',
      forceNiceScale: true,
      labels: {
        rotate: -45,
        formatter: (val) => {
          const date = new Date(val);
          return `${date.getDate()}/${date.getMonth() + 1}`;
        },
      },
    },
    yaxis: {
      title: { text: 'Number of Leads' },
      min: 0,
      forceNiceScale: true,
    },
    title: {
      text:
        status && managerId
          ? `Leads by Date (${status} - ${managers.find((m) => m._id === managerId)?.name || 'Selected Manager'})`
          : status
            ? `Leads by Date (${status})`
            : managerId
              ? `Leads by Date (${managers.find((m) => m._id === managerId)?.name || 'Selected Manager'})`
              : 'Leads by Date',
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
      },
    },
    subtitle: {
      text: ` ${totalLeads} total leads`,
      align: 'left',
    },
    stroke: { curve: 'smooth' },
    dataLabels: { enabled: false },
    colors: [chartColor],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        opacityFrom: 0.7,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    tooltip: {
      theme: tooltipTheme,
      y: {
        formatter: (val) => `${val} leads`,
      },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const rawDate = xCategories[dataPointIndex];
          const date = new Date(rawDate);
          return date.toDateString();
        },
      },
    },
  };

  return (
    <Box
      bg={chartBg}
      p={{ base: 3, md: 6 }}
      borderRadius="xl"
      shadow="md"
      h={{ base: 'auto', md: '500px' }}
      w="100%"
    >
      {/* <Text fontSize="xl" fontWeight="bold" mb={4}> */}
      <Text fontSize={{ base: 'md', md: 'xl' }} fontWeight="bold" mb={4}>
        Leads by Date
      </Text>
      <Flex
        mb={6}
        gap={4}
        flexWrap="wrap"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-end"
      >
        {/* <Flex gap={4} flexWrap="wrap" flex={1}> */}
        <Flex gap={4} flexWrap="wrap" flex={1} w={{ base: '100%', md: 'auto' }}>
          <FormControl w={{ base: '100%', md: '45%' }}>
            <FormLabel fontSize="sm">Start Date</FormLabel>
            <Input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              max={endDate}
            />
          </FormControl>

          <FormControl w={{ base: '100%', md: '45%' }}>
            <FormLabel fontSize="sm">End Date</FormLabel>
            <Input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              min={startDate}
              max={getDefaultEndDate()}
            />
          </FormControl>
        </Flex>
        {/* <Flex gap={4} alignItems="flex-end"> */}
        <Flex
          gap={4}
          alignItems="flex-end"
          flexWrap="wrap"
          w={{ base: '100%', md: 'auto' }}
        >
          {localStorage.getItem('type') !== 'Manager' && (
            <FormControl w={{ base: '100%', md: '300px' }}>
              <FormLabel fontSize="sm">Manager</FormLabel>
              <Select
                value={managerId}
                onChange={handleManagerChange}
                placeholder="Select Manager"
                bg="white"
              >
                {managers.map((manager) => (
                  <option key={manager._id} value={manager._id}>
                    {manager.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl w={{ base: '100%', md: '300px' }}>
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select
              value={status}
              onChange={handleStatusChange}
              placeholder="Select Lead Status"
              bg="white"
            >
              {setting?.getSetting?.leadStatuses?.leadStatuses?.length > 0 &&
                setting?.getSetting?.leadStatuses?.leadStatuses?.map(
                  (status) => (
                    <option key={status._id} value={status.title}>
                      {status.title}
                    </option>
                  ),
                )}
            </Select>
          </FormControl>

          <Button
            colorScheme="blue"
            onClick={() => {
              setStartDate(getDefaultStartDate());
              setEndDate(getDefaultEndDate());
              setStatus('');
              setManagerId(''); // Add this line to reset managerId
            }}
            h="40px"
            w={{ base: '100%', md: 'auto' }}
          >
            Reset
          </Button>
        </Flex>
      </Flex>

      {/* <Box w="100%" h="300px"> */}
      <Box w="100%" h={{ base: '250px', md: '300px' }}>
        {isLoading ? (
          <Center h="100%">
            <Spinner size="xl" color={chartColor} />
          </Center>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartData}
            type="area"
            width="100%"
            height="100%"
          />
        )}
      </Box>
    </Box>
  );
};

export default TotalLeadsChart;
